import { useEffect, useState } from "react";
import type { Settlement, Status, Structure } from "../lib/api";
import { BiomePicker } from "./BiomePicker";
import { NamePicker } from "./NamePicker";

// Only trimming is left. Rotating and mirroring a source file is not curation
// -- the generator already turns each build per site -- and the buttons stood
// where the one control that matters, the height, belongs.
export type GeometryOp = "trim";

// Everything the world generator needs to know about a build.
//
// Built around what a curator actually does a thousand times: look at the
// build, nudge how it sits, judge it. So the two ends are FIXED -- what this
// is at the top, the verdict at the bottom -- and only the middle scrolls.
// The verdict used to sit below a dozen open fields, which meant scrolling to
// the thing you press every single time.
//
// The fields in between are collapsed and show their CURRENT VALUE in the
// header, so the whole rule can be checked at a glance and only what is wrong
// has to be opened. Most builds in a kit need no edit at all.
//
// Edits are sent as they are made: a shared queue must not depend on someone
// remembering to press save.

function Section(props: {
  title: string;
  summary: string;
  warn?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className={open ? "sec open" : "sec"}>
      <button className="sechead" onClick={() => setOpen((o) => !o)}>
        <span className="caret">{open ? "▾" : "▸"}</span>
        <span className="what">{props.title}</span>
        <span className={props.warn ? "value warn" : "value"}>
          {props.summary}
        </span>
      </button>
      {open && <div className="secbody">{props.children}</div>}
    </section>
  );
}

// What is still open on this build. Nothing here blocks the verdict -- these
// are the settings that fail SILENTLY in the generator, so they are said out
// loud while the build is still on screen.
function openPoints(s: Structure, home: Settlement | null): string[] {
  const out: string[] = [];
  // The settlement's own verdicts, said here as well: a curator judging this
  // build should not have to visit another tab to learn that it will never be
  // placed.
  if (home) out.push(...home.problems);
  if (s.placement === 1 && s.yMin === 0 && s.yMax === 0)
    out.push("unterirdisch ohne Höhenband — landet in jeder Tiefe");
  if (s.weight === 0) out.push("Gewicht 0 — wird nie gewählt");
  // A wayside prop is scattered along trails, never grown into a village: the
  // settlement passes only ever look at landmarks.
  if (s.use === 1 && s.role !== 0)
    out.push("steht am Wegrand, ist aber Teil einer Siedlung");
  return out;
}

const PLACEMENT = ["Oberfläche", "unterirdisch", "schwebend", "im Wasser"];

// What a build IS. The category decides which pass may place it and travels
// into the export, so a plugin can tell a dungeon from a market stall.
export const USE = [
  "Wahrzeichen",
  "am Wegrand",
  "Dungeon",
  "Dungeon-Eingang",
  "Arena",
  "Ruine",
  "Lager",
  "Händler / Rast",
];

// The band a rule matches. It is stored as 0..3, but nobody thinks in numbers:
// for a landmark the number is the rarity of the site, for a wayside prop it is
// the class of the way it may stand at. Same field, two vocabularies.
const RARITY = ["gewöhnlich", "ungewöhnlich", "selten", "episch"];
const WAY = ["Trampelpfad", "Feldweg", "Straße", "Hauptstraße"];

function bandLabel(use: number, lo: number, hi: number): string {
  const names = use === 1 ? WAY : RARITY;
  if (lo === 0 && hi === 3) return "überall";
  if (lo === hi) return names[lo] ?? "?";
  return `${names[lo]} – ${names[hi]}`;
}

export function Inspector(props: {
  structure: Structure | null;
  busy: boolean;
  // Set to the holder's name when somebody else is working on this build; the
  // form is then look-but-do-not-touch.
  readOnly: string | null;
  onPatch: (fields: Record<string, unknown>) => void | Promise<void>;
  onDecide: (s: Status) => void;
  // Copy this build's settings onto its variant siblings or its whole folder.
  onBatch: (scope: "group" | "folder", withStatus: boolean) => void;
  // Only to report a settlement's problems next to the verdict: a build whose
  // settlement has no anchor is never placed, and that must not be invisible
  // just because settlements are edited in another tab.
  settlements: Settlement[];
  onGeometry: (op: GeometryOp) => void;
  onClaim: () => void;
}) {
  const s = props.structure;
  const [note, setNote] = useState("");
  const [name, setName] = useState("");

  // Text fields are local while typing and pushed on blur, so every keystroke
  // is not a request.
  useEffect(() => {
    setNote(s?.note ?? "");
    setName(s?.name ?? "");
  }, [s?.id, s?.note, s?.name]);

  // The empty state is the first thing a new curator sees, so it says how the
  // loop works instead of only that nothing is open.
  if (!s)
    return (
      <aside className="inspector empty">
        <button className="primary" onClick={props.onClaim}>
          Nächste freie holen <kbd>Strg</kbd>+<kbd>N</kbd>
        </button>
        <ol className="howto">
          <li>ansehen — ziehen dreht, scrollen zoomt</li>
          <li>sitzt sie richtig? sonst Höhe anpassen</li>
          <li>übernehmen, zurückstellen oder aussortieren</li>
        </ol>
        <p className="hint">Gespeichert wird sofort.</p>
      </aside>
    );

  const patch = props.readOnly ? () => {} : props.onPatch;
  const home = props.settlements.find((x) => x.id === s.settlementId) ?? null;
  const locked = Boolean(props.readOnly);
  const points = openPoints(s, home);
  // A single pattern with no placeholders in it is a fixed name; show the name
  // itself rather than the vocabulary it is nominally filed under.
  const nameParts = s.nameStyle.split("|").filter(Boolean);
  const nameSummary = !s.nameStyle
    ? "keiner"
    : nameParts.length === 2 && !nameParts[1].includes("{")
      ? `„${nameParts[1]}“`
      : `gewürfelt · ${nameParts[0].split(":").pop()}`;

  return (
    <aside className={locked ? "inspector locked" : "inspector"}>
      {/* --- what this is: pinned, because it is the answer to "which build am
          I looking at" and must survive scrolling --- */}
      <header className="insp-head">
        <h2>{s.name}</h2>
        <p className="meta">
          {s.folder || "(kein Ordner)"}
          {s.sizeX > 0 && ` · ${s.sizeX}×${s.sizeY}×${s.sizeZ}`}
          {s.geometryEdited && " · bearbeitet"}
          {!locked && (
            <button
              className="linkish trim"
              onClick={() => props.onGeometry("trim")}
              title="Schneidet leere Ebenen am Rand des Bauwerks ab und verkleinert damit seine Grundfläche. Ändert die Schematic-Datei."
            >
              leeren Rand abschneiden
            </button>
          )}
        </p>
        {locked && <p className="held">🔒 {props.readOnly} bearbeitet gerade</p>}

        {/* The one control that is used on nearly every build, next to the
            view it is judged in -- everything else is a rarity. */}
        <div className="seat">
          <span>Höhe</span>
          <button
            disabled={locked}
            onClick={() => patch({ yOffset: Math.max(-64, s.yOffset - 1) })}
            title="einen Block tiefer"
          >
            −
          </button>
          <b>{s.yOffset > 0 ? `+${s.yOffset}` : s.yOffset}</b>
          <button
            disabled={locked}
            onClick={() => patch({ yOffset: Math.min(64, s.yOffset + 1) })}
            title="einen Block höher"
          >
            +
          </button>
          {/* What happens to the build's air below the surface. It belongs
              beside the height because it only matters once you lower one. */}
          <label
            className="check bury"
            title="Nur die Luft ausheben, die das Bauwerk selbst umschließt — der Zwischenraum nach außen bleibt Boden. Ohne das steht ein abgesenkter Bau in einer rechteckigen Wanne."
          >
            <input
              type="checkbox"
              checked={s.bury}
              disabled={locked}
              onChange={(e) => patch({ bury: e.target.checked })}
            />
            eingraben
          </label>

        </div>
      </header>

      {/* --- the rule: readable closed, editable open --- */}
      <fieldset disabled={locked} className="insp-body">
        <Section
          title="Verwendung"
          summary={USE[s.use] ?? "?"}
          warn={s.use === 2}
        >
          <select
            title="Entscheidet, welcher Pass das Bauwerk platziert: Wegrand-Kleinkram steht neben Wegen, alles andere an einer Fundstelle."
            value={s.use}
            onChange={(e) => patch({ use: Number(e.target.value) })}
          >
            {USE.map((u, i) => (
              <option key={u} value={i}>
                {u}
              </option>
            ))}
          </select>
          {s.use === 2 && (
            <p className="hint">
              Dungeons werden noch nicht platziert — der Untergrund-Spawner
              kann bisher nur prozedurale Räume. Kuratieren lohnt trotzdem:
              sie landen in der Bibliothek und im Export.
            </p>
          )}
        </Section>

        <Section
          title="Wo"
          summary={
            PLACEMENT[s.placement] +
            (s.biomes.length ? ` · ${s.biomes.length} Biome` : "") +
            (s.yMin || s.yMax ? ` · Y ${s.yMin}–${s.yMax}` : "")
          }
          warn={s.placement === 1 && !s.yMin && !s.yMax}
        >
          <label>
            Umgebung
            <select
              value={s.placement}
              onChange={(e) => patch({ placement: Number(e.target.value) })}
            >
              {PLACEMENT.map((p, i) => (
                <option key={p} value={i}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label title="Höhenband, in dem der Bau stehen darf. 0 / 0 = egal.">
            Höhe von … bis
            <span className="row">
              <input
                type="number"
                value={s.yMin}
                onChange={(e) => patch({ yMin: Number(e.target.value) })}
              />
              <input
                type="number"
                value={s.yMax}
                onChange={(e) => patch({ yMax: Number(e.target.value) })}
              />
            </span>
          </label>
          <BiomePicker value={s.biomes} onChange={(biomes) => patch({ biomes })} />
        </Section>

        <Section
          title="Wie oft"
          summary={`${bandLabel(s.use, s.tierMin, s.tierMax)} · ×${s.weight}`}
          warn={s.weight === 0}
        >
          <label
            title={
              s.use === 1
                ? "Die Wegklassen, an denen dieses Ding stehen darf."
                : "Die Ränge der Fundstellen, an denen dieser Bau vorkommen darf."
            }
          >
            {s.use === 1 ? "Wegklasse" : "Rang"}
            <span className="row">
              <select
                value={s.tierMin}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  patch({ tierMin: v, tierMax: Math.max(v, s.tierMax) });
                }}
              >
                {(s.use === 1 ? WAY : RARITY).map((n, i) => (
                  <option key={n} value={i}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="upto">bis</span>
              <select
                value={s.tierMax}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  patch({ tierMax: v, tierMin: Math.min(v, s.tierMin) });
                }}
              >
                {(s.use === 1 ? WAY : RARITY).map((n, i) => (
                  <option key={n} value={i}>
                    {n}
                  </option>
                ))}
              </select>
            </span>
          </label>
          <label title="Wie oft dieser Bau gegenüber den anderen gezogen wird.">
            Gewicht
            <input
              type="number"
              step={0.25}
              min={0}
              value={s.weight}
              onChange={(e) => patch({ weight: Number(e.target.value) })}
            />
          </label>
          <label className="check" title="Der Generator ebnet nur den Untergrund ein; den Bau selbst setzt später ein Plugin.">
            <input
              type="checkbox"
              checked={s.prepareOnly}
              onChange={(e) => patch({ prepareOnly: e.target.checked })}
            />
            nur Boden vorbereiten
          </label>
        </Section>

        <Section
          title="Name"
          summary={nameSummary}
        >
          <NamePicker
            value={s.nameStyle}
            seed={s.id}
            onChange={(nameStyle) => patch({ nameStyle })}
          />
          <label title="Steht nur hier im Studio.">
            interner Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name !== s.name && patch({ name })}
            />
          </label>
        </Section>

        <Section title="Notiz" summary={note.trim() ? "vorhanden" : "—"}>
          <textarea
            value={note}
            placeholder="z. B. „Dach fehlt“"
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => note !== s.note && patch({ note })}
          />
        </Section>

        {/* A kit ships the same build for six biomes; deciding each one over
            again is busywork. */}
        <div className="batch">
          {s.variantGroup && (
            <button
              type="button"
              title={`Einstellungen und Urteil auf alle Varianten von „${s.variantGroup}“ übertragen`}
              onClick={() => props.onBatch("group", true)}
            >
              ◇ auf alle Varianten
            </button>
          )}
          <button
            type="button"
            title="Einstellungen auf alle noch offenen Bauten dieses Ordners übertragen"
            onClick={() => props.onBatch("folder", false)}
          >
            auf den Ordner
          </button>
        </div>
      </fieldset>

      {/* --- the verdict: pinned, because it is pressed on every build --- */}
      <footer className="insp-foot">
        {points.length > 0 && (
          <ul className="points">
            {points.map((p, i) => (
              <li key={i}>⚠ {p}</li>
            ))}
          </ul>
        )}
        <div className="verdict">
          <button
            className="approve"
            disabled={props.busy || locked}
            onClick={() => props.onDecide("approved")}
            title="Strg+Enter"
          >
            ✓ Übernehmen
          </button>
          <button
            disabled={props.busy || locked}
            onClick={() => props.onDecide("later")}
            title="Strg+L"
          >
            …
          </button>
          <button
            disabled={props.busy || locked}
            onClick={() => props.onDecide("rejected")}
            title="Strg+Rücktaste"
          >
            ✕
          </button>
        </div>
      </footer>
    </aside>
  );
}
